import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Upload, message } from 'antd';
import { QrcodeOutlined, CameraOutlined, FileImageOutlined } from '@ant-design/icons';
import QrScanner from 'qr-scanner';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ModalContent = styled.div`
  .qr-scanner-container {
    position: relative;
    min-height: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    
    @media (max-width: 768px) {
      padding: 20px;
      min-height: 450px;
    }
  }

  .camera-section {
    width: 100%;
    max-width: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
  }

  .camera-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 18px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 20px;
    
    .anticon {
      font-size: 20px;
      color: #9BC53D;
    }
  }

  .file-upload-section {
    width: 100%;
    max-width: 450px;
  }

  .upload-header {
    margin-bottom: 20px;
    
    .back-to-camera {
      color: #9BC53D;
      font-weight: 500;
      
      &:hover {
        color: #7BA428;
        background: rgba(155, 197, 61, 0.1);
      }
    }
  }

  .qr-scanner-reader {
    width: 100%;
    border-radius: 16px;
    overflow: hidden;
    background: #000;
    position: relative;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border: 2px solid #E8EAED;
    
    .qr-video {
      width: 100% !important;
      height: auto !important;
      min-height: 320px;
      object-fit: cover;
      display: block;
    }
    
    @media (max-width: 768px) {
      .qr-video {
        min-height: 280px;
      }
    }
  }

  .upload-divider {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 300px;
    margin: 32px 0 24px 0;
    
    .divider-line {
      flex: 1;
      height: 1px;
      background: #E8EAED;
    }
    
    .divider-text {
      padding: 0 16px;
      color: #5F6368;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .upload-toggle-btn {
    border-radius: 12px !important;
    height: 48px !important;
    padding: 0 24px !important;
    border: 2px solid #E8EAED !important;
    color: #5F6368 !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      border-color: #9BC53D !important;
      color: #9BC53D !important;
      background: rgba(155, 197, 61, 0.05) !important;
    }
    
    .anticon {
      margin-right: 8px;
    }
  }

  .qr-scanner-status {
    text-align: center;
    padding: 20px;
    color: #5F6368;
    font-size: 14px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    backdrop-filter: blur(8px);
    
    .anticon {
      margin-right: 8px;
    }
  }

  .qr-scanner-permission {
    text-align: center;
    padding: 40px 20px;
    color: #5F6368;
    
    .permission-icon {
      font-size: 48px;
      color: #9BC53D;
      margin-bottom: 16px;
    }
    
    .permission-title {
      font-size: 16px;
      font-weight: 600;
      color: #1F2937;
      margin-bottom: 8px;
    }
    
    .permission-desc {
      font-size: 14px;
      line-height: 1.5;
      margin-bottom: 20px;
    }
    
    .permission-button {
      background: linear-gradient(135deg, #9BC53D, #7BA428);
      border: none;
      border-radius: 8px;
      color: white;
      padding: 12px 24px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: linear-gradient(135deg, #8AB12A, #6A9320);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(155, 197, 61, 0.3);
      }
    }
  }

  .upload-area {
    width: 100%;
    border: 2px dashed #D9D9D9;
    border-radius: 16px;
    padding: 48px 24px;
    text-align: center;
    background: #FAFAFA;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 240px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    
    &:hover {
      border-color: #9BC53D;
      background: #F0F8E6;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(155, 197, 61, 0.15);
    }
    
    .upload-icon {
      font-size: 56px;
      color: #9BC53D;
      margin-bottom: 20px;
    }
    
    .upload-title {
      font-size: 18px;
      font-weight: 600;
      color: #1F2937;
      margin-bottom: 12px;
    }
    
    .upload-desc {
      color: #5F6368;
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .qr-scanner-result {
    background: linear-gradient(135deg, #F0F8E6, #E8F5E0);
    border: 2px solid #9BC53D;
    border-radius: 16px;
    padding: 24px;
    margin-top: 24px;
    word-break: break-all;
    width: 100%;
    max-width: 450px;
    box-shadow: 0 4px 16px rgba(155, 197, 61, 0.2);
    
    .result-title {
      color: #7BA428;
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 12px;
      text-align: center;
    }
    
    .result-text {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      background: white;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid #D9D9D9;
      line-height: 1.4;
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }

  .qr-scanner-error {
    background: #FFF2F0;
    border: 1px solid #FF4D4F;
    border-radius: 12px;
    padding: 20px;
    margin-top: 16px;
    color: #FF4D4F;
    text-align: center;
    width: 100%;
    
    .error-icon {
      font-size: 24px;
      margin-bottom: 8px;
    }
    
    .error-title {
      font-weight: 600;
      margin-bottom: 8px;
    }
    
    .error-desc {
      font-size: 14px;
      margin-bottom: 16px;
    }
  }
`;

interface QRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (text: string) => void;
}

const QRScannerModal: React.FC<QRScannerModalProps> = ({
  isOpen,
  onClose,
  onScanSuccess,
}) => {
  const { t } = useTranslation();
  const [scanResult, setScanResult] = useState<string>('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);

  // Initialize QR scanner when modal opens and camera is ready
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isOpen && !showFileUpload) {
      console.log('🎥 Modal opened, preparing QR scanner...');
      
      // Add proper delay to ensure modal and video element are fully rendered
      timeoutId = setTimeout(() => {
        if (videoRef.current) {
          console.log('📹 Video element ready, starting QR scanner...');
          const video = videoRef.current;
          
          const qrScanner = new QrScanner(
            video,
            (result) => {
              console.log('✅ QR decoded:', result.data);
              setScanResult(result.data);
              message.success(t('#Task.qr_scan_success'));
              onScanSuccess(result.data);
            },
            {
              returnDetailedScanResult: true,
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );
          
          qrScannerRef.current = qrScanner;
          qrScanner.start().then(() => {
            console.log('✅ QR scanner started successfully');
          }).catch((error) => {
            console.error('❌ Failed to start QR scanner:', error);
            // Retry once after another delay
            setTimeout(() => {
              if (qrScannerRef.current) {
                qrScannerRef.current.start().then(() => {
                  console.log('✅ QR scanner started on retry');
                }).catch((retryError) => {
                  console.error('❌ QR scanner failed on retry:', retryError);
                });
              }
            }, 500);
          });
        } else {
          console.log('❌ Video element not ready, retrying...');
          // If video element is not ready, try again
          setTimeout(() => {
            if (videoRef.current && isOpen && !showFileUpload) {
              // Trigger the effect again by setting a flag or just retry here
              const video = videoRef.current;
              const qrScanner = new QrScanner(
                video,
                (result) => {
                  console.log('✅ QR decoded:', result.data);
                  setScanResult(result.data);
                  message.success(t('#Task.qr_scan_success'));
                  onScanSuccess(result.data);
                },
                {
                  returnDetailedScanResult: true,
                  highlightScanRegion: true,
                  highlightCodeOutline: true,
                }
              );
              
              qrScannerRef.current = qrScanner;
              qrScanner.start().then(() => {
                console.log('✅ QR scanner started on video retry');
              }).catch((error) => {
                console.error('❌ QR scanner failed on video retry:', error);
              });
            }
          }, 300);
        }
      }, 500); // Increased delay to 500ms
      
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (qrScannerRef.current) {
          console.log('🛑 Cleaning up QR scanner');
          qrScannerRef.current.stop();
          qrScannerRef.current.destroy();
          qrScannerRef.current = null;
        }
      };
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, showFileUpload, t, onScanSuccess]);

  // Force re-initialize scanner when coming back from file upload
  useEffect(() => {
    if (!showFileUpload && isOpen && videoRef.current && qrScannerRef.current === null) {
      // Trigger re-initialization by updating a dependency
      const timer = setTimeout(() => {
        // This will trigger the main useEffect above
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [showFileUpload, isOpen]);

  // Handle file upload for QR scanning
  const handleFileUpload = async (file: File) => {
    try {
      setScanResult('');
      
      const result = await QrScanner.scanImage(file, {
        returnDetailedScanResult: true,
      });
      
      setScanResult(result.data);
      message.success(t('#Task.qr_scan_success'));
      onScanSuccess(result.data);
    } catch (err) {
      message.error(t('#Task.qr_scan_error'));
    }
    
    return false;
  };

  // Cleanup on modal close
  useEffect(() => {
    if (!isOpen) {
      setScanResult('');
      setShowFileUpload(false);
    }
  }, [isOpen]);

  const handleModalClose = () => {
    onClose();
  };

  const toggleFileUpload = () => {
    setShowFileUpload(!showFileUpload);
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <QrcodeOutlined />
          {t('#Task.qr_scan_title')}
        </div>
      }
      open={isOpen}
      onCancel={handleModalClose}
      footer={null}
      width={500}
      centered
      destroyOnClose
    >
      <ModalContent>
        <div className="qr-scanner-container">
          {!showFileUpload ? (
            <>
              {/* Primary Camera Scanner */}
              <div className="camera-section">
                <div className="camera-title">
                  <CameraOutlined />
                  <span>{t('#Task.qr_scan_camera')}</span>
                </div>
                <div className="qr-scanner-reader">
                  <video 
                    ref={videoRef} 
                    className="qr-video"
                  />
                </div>
              </div>
              
              {/* Divider and File Upload Option */}
              <div className="upload-divider">
                <div className="divider-line"></div>
                <span className="divider-text">{t('#Task.qr_scan_or')}</span>
                <div className="divider-line"></div>
              </div>
              
              <Button 
                type="default"
                icon={<FileImageOutlined />}
                onClick={toggleFileUpload}
                className="upload-toggle-btn"
                size="large"
              >
                {t('#Task.qr_scan_file')}
              </Button>
            </>
          ) : (
            <>
              {/* File Upload Mode */}
              <div className="file-upload-section">
                <div className="upload-header">
                  <Button 
                    type="text"
                    icon={<CameraOutlined />}
                    onClick={toggleFileUpload}
                    className="back-to-camera"
                  >
                    ← Back to Camera
                  </Button>
                </div>
                
                <Upload.Dragger
                  name="qr-image"
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  accept="image/*"
                  className="upload-area"
                >
                  <div className="upload-icon">
                    <FileImageOutlined />
                  </div>
                  <div className="upload-title">
                    {t('#Task.qr_scan_file')}
                  </div>
                  <div className="upload-desc">
                    Click or drag QR code image to scan
                  </div>
                </Upload.Dragger>
              </div>
            </>
          )}
          
          {/* Success Result */}
          {scanResult && (
            <div className="qr-scanner-result">
              <div className="result-title">✅ {t('#Task.qr_scan_success')}</div>
              <div className="result-text">{scanResult}</div>
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default QRScannerModal;